import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select.tsx';
import { Button } from './button.tsx';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export function BaseTable({
  columns,
  data,
  rowActions,
  onRowClick,
  pageSize = 10,
  currentPage = 1,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg min-h-[300px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading data...</p>
        </div>
      </div>
    );
  }

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <div className="bg-white rounded-lg min-h-[300px] flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p className="text-sm">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50 hover:bg-gray-50/50 border-b border-gray-200">
              {columns.map((column) => (
                <TableHead 
                  key={column.key}
                  className="text-xs font-semibold text-gray-600 h-11 px-6 py-3"
                >
                  {column.label}
                </TableHead>
              ))}
              {rowActions && <TableHead className="w-[48px] px-6" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={row.id || index}
                className={`
                  ${onRowClick ? 'cursor-pointer' : ''}
                  hover:bg-gray-50/50
                  transition-colors
                  border-b border-gray-100
                  group
                `}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <TableCell 
                    key={`${row.id || index}-${column.key}`}
                    className="px-6 py-4"
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </TableCell>
                ))}
                {rowActions && (
                  <TableCell className="w-[48px] px-6">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      {rowActions(row)}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalItems > 10 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange?.(Number(value))}
          >
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue placeholder="Rows per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 rows per page</SelectItem>
              <SelectItem value="20">20 rows per page</SelectItem>
              <SelectItem value="50">50 rows per page</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {Math.min((currentPage - 1) * pageSize + 1, totalItems)}-
              {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => onPageChange?.(currentPage - 1)}
              >
                <span className="sr-only">Previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange?.(currentPage + 1)}
              >
                <span className="sr-only">Next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}